import * as Sentry from "@sentry/nextjs";
import * as redux from "redux";
import thunk from "redux-thunk";
import { ApiConfig } from "~/api-client";
import { storage } from "~/components/utils/storage";
import {
  DeliveryAddress,
  OrderDispatchOption,
  OrderDraft,
  TimeSlot,
} from "~/types";
import type { Action } from "./action";
import { reducer as deliveryAddress } from "./delivery-address";
import type { DeliveryAddressState } from "./delivery-address";
import { ApiOrderingService, reducer as ordering } from "./ordering";
import type { OrderingState } from "./ordering";
import { Context, ThunkDispatch } from "./thunk";

export type { Action };
export interface AppState {
  readonly deliveryAddress: DeliveryAddressState;
  readonly ordering: OrderingState;
}

const reducer = redux.combineReducers<AppState, Action>({
  deliveryAddress,
  ordering,
});

type Store = redux.Store<redux.EmptyObject & AppState, Action> & {
  dispatch: ThunkDispatch;
};

export function createStore(apiConfig: ApiConfig): Store {
  const store = redux.createStore(
    reducer,
    reducer(undefined, init()),
    redux.applyMiddleware<ThunkDispatch, AppState>(
      thunk.withExtraArgument<Context>({
        getOrderService: () => new ApiOrderingService(apiConfig),
      }),
    ),
  );

  store.subscribe(() => {
    storeState(store.getState());
  });

  return store;
}

export function init(): Action {
  return {
    type: "INIT_ACTION",
    ...loadState(),
  };
}

interface PersistentState {
  orderDrafts: Record<string, OrderDraft>;
  deliveryAddresses: DeliveryAddress[];
  orderDispatchOption: OrderDispatchOption | null;
  serviceChargeRemoved: boolean;
  timeSlot: TimeSlot | null;
  voucherCode: string | null;
}

const emptyPersistentState: PersistentState = {
  orderDrafts: {},
  deliveryAddresses: [],
  orderDispatchOption: null,
  serviceChargeRemoved: false,
  timeSlot: null,
  voucherCode: null,
};

function loadState(): PersistentState {
  try {
    const ls = storage();
    const stateStr = ls.getItem("user_state");
    if (stateStr) {
      // Partial because we can't be sure old versions had the same fields
      const state: Partial<PersistentState> = JSON.parse(stateStr);

      return { ...emptyPersistentState, ...state };
    }
  } catch (err) {
    Sentry.captureException(err);
  }

  return emptyPersistentState;
}

function storeState(state: AppState) {
  const { ordering, deliveryAddress } = state;
  const ls = storage();

  const orderDrafts: Record<string, OrderDraft> = {};
  Object.keys(ordering.orderingByLocation).forEach((locId) => {
    const { cart, deliveryAddress, notes, serviceRef } =
      ordering.orderingByLocation[locId];
    orderDrafts[locId] = {
      cart,
      deliveryAddress,
      notes,
      serviceRef,
    };
  });

  const ps: PersistentState = {
    orderDrafts,
    deliveryAddresses: deliveryAddress.deliveryAddresses,
    orderDispatchOption: ordering.dispatchOption,
    serviceChargeRemoved: ordering.serviceChargeRemoved,
    timeSlot: ordering.dispatchTime,
    voucherCode: ordering.voucherCode,
  };

  try {
    ls.setItem("user_state", JSON.stringify(ps));
  } catch (err) {
    // This seems to happen on old versions of iOS
    // with "The quota has been exceeded."
    console.warn("Failed to persist state to local storage");
    // Logging this to sentry might just be noise, remove if not useful
    Sentry.withScope((scope) => {
      scope.setLevel("warning");
      Sentry.captureException(err);
    });
  }
}
